/**
 * PURE echarts option builder for the RANKED_BAR chart (horizontal top-N taxa ranking).
 *
 * Kept separate from the component so a future server-side render path can reuse the same option
 * builder. This module is
 * React/DOM/Redux-free: its only runtime imports are the pure `visualIdentity` and the pure
 * `vizFormatters`. All echarts imports are TYPE-ONLY (erased at build), so nothing from echarts'
 * runtime is pulled in here — the builder just returns a plain option object.
 *
 * Keep every visual detail byte-identical to the component (background, palette fallback, inverse
 * category axis, compact tick formatter) and keep `animation:false` — it is mandatory for SSR.
 */
import type { ComposeOption } from 'echarts/core'
import type { BarSeriesOption } from 'echarts/charts'
import type { GridComponentOption, TooltipComponentOption } from 'echarts/components'
import type { XAXisComponentOption, YAXisComponentOption } from 'echarts'
import { visTickFormatter } from './vizFormatters.js'
import { CHART_SURFACE, fillStyleFor, identityFor } from './visualIdentity.js'

export type RankedBarEChartsOption = ComposeOption<
  BarSeriesOption | GridComponentOption | TooltipComponentOption | XAXisComponentOption | YAXisComponentOption
>

export interface RankedBar {
  name: string
  value: number
}

export interface RankedBarOptionInput {
  /** Bars in ranked order (highest first); rendered top-to-bottom via the inverse category axis. */
  data: RankedBar[]
  /** x-axis name; defaults to 'Reads' when omitted (mirrors the component prop default). */
  xlabel?: string
}

/** Build the echarts option for a horizontal ranked-bar chart. Pure `input -> option` map. */
export function rankedBarOption({ data, xlabel }: RankedBarOptionInput): RankedBarEChartsOption {
  const names = data.map((d) => d.name)
  const maxValue = Math.max(1, ...data.map((d) => d.value))
  return {
    backgroundColor: CHART_SURFACE,
    animation: false,
    tooltip: {
      trigger: 'item',
      confine: true,
      valueFormatter: (v) => (typeof v === 'number' ? (visTickFormatter(v) ?? String(v)) : String(v ?? ''))
    },
    grid: { top: 12, bottom: 40, left: 8, right: 24, containLabel: true },
    xAxis: {
      type: 'value',
      name: xlabel ?? 'Reads',
      nameLocation: 'middle',
      nameGap: 28,
      min: 0,
      axisLabel: { formatter: (val: number) => visTickFormatter?.(val) ?? String(val) }
    },
    yAxis: {
      type: 'category',
      // highest-ranked bar at the TOP (data is descending, category axis is bottom-up by default)
      inverse: true,
      data: names,
      axisLabel: { fontSize: 10, hideOverlap: false }
    },
    series: [
      {
        type: 'bar',
        data: data.map((d) => ({ value: d.value, itemStyle: fillStyleFor(identityFor(d.name), d.value / maxValue) })),
        emphasis: { focus: 'self' }
      }
    ]
  }
}
